import { useEffect, useState } from "react";
import { FundCalendar } from "@/types";
import { useAtom } from "jotai";
import { ClientStateAtom, FundCalendarStateAtom } from "@/store";
import { saveFundCalendar } from "@/api/FundCalendar";
import { toast } from "sonner";

export interface FundCalendarFormModalProps {
  isOpen: boolean;
  onClose: (refreshData?: boolean) => void;
  calendar?: FundCalendar | null;
}

export const useFundCalendarFormModalController = (
  props: FundCalendarFormModalProps
) => {
  const { isOpen, onClose, calendar } = props;
  const [selectedClient] = useAtom(ClientStateAtom);
  const [fundCalendars] = useAtom(FundCalendarStateAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFirstCalendar, setIsFirstCalendar] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FundCalendar>({
    clientId: selectedClient?.clientId ?? 0,
    name: "",
    intervalDefinition: "monthly-first-day",
    isDefault: false,
  });

  // Check if this is the first calendar
  useEffect(() => {
    // This is the first calendar if:
    // 1. Creating a new calendar when no calendars exist, OR
    // 2. Editing the only existing calendar
    if (
      (!calendar && fundCalendars.length === 0) ||
      (calendar && fundCalendars.length === 1)
    ) {
      setIsFirstCalendar(true);
    } else {
      setIsFirstCalendar(false);
    }
  }, [calendar, fundCalendars]);

  // Initialize form with existing calendar data when editing
  useEffect(() => {
    if (calendar) {
      setFormData({
        ...calendar,
      });
    } else {
      // Reset form for new calendar
      setFormData({
        clientId: selectedClient?.clientId ?? 0,
        name: "",
        intervalDefinition: "monthly-first-day",
        isDefault: isFirstCalendar, // Set default to true for first calendar
      });
    }
  }, [calendar, selectedClient, isFirstCalendar]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIntervalChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      intervalDefinition: value,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    // If this is the first calendar, we don't allow changing isDefault
    if (isFirstCalendar) return;

    setFormData((prev) => ({
      ...prev,
      isDefault: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClient) {
      toast.error("No client selected. Please select a client first.");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Calendar name is required");
      return;
    }

    // Ensure the first calendar is always set as default
    const dataToSave = {
      ...formData,
      isDefault: isFirstCalendar ? true : formData.isDefault,
    };

    setIsSubmitting(true);

    try {
      const success = await saveFundCalendar(dataToSave);

      if (success) {
        toast.success(
          `Fund calendar ${calendar ? "updated" : "created"} successfully`
        );
        onClose(true); // Close and refresh data
      } else {
        toast.error("Failed to save fund calendar");
      }
    } catch (error) {
      console.error("Error saving fund calendar:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isOpen,
    onClose,
    calendar,
    isSubmitting,
    formData,
    isFirstCalendar,
    handleInputChange,
    handleIntervalChange,
    handleCheckboxChange,
    handleSubmit,
  };
};
